import { useState } from 'react';
import { Shield, ShieldCheck, LogOut, User, Menu, X, Database, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { auth } from '../lib/auth';
import { db } from '../lib/db';
import { analyticsCache } from '../lib/cache';
import { formatTime } from '../lib/utils';
import type { User as UserType } from '../types';

interface LayoutProps {
  children: React.ReactNode;
  user: UserType | null;
  onRefresh: () => void;
  lastSynced: string | null;
  isAdmin: boolean;
  impersonating: boolean;
  onToggleImpersonate: () => void;
  onOpenAdminPanel: () => void;
}

export function Layout({ children, user, onRefresh, lastSynced, isAdmin, impersonating, onToggleImpersonate, onOpenAdminPanel }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDevTools, setShowDevTools] = useState(false);

  return (
    <div className="min-h-screen bg-oem-50 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-oem-navy shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img
                src="https://www.oemstream.com/static/version1773239015/frontend/Hyva/oemstream/en_US/images/logo.png"
                alt="OEMSTREAM"
                className="h-8 w-auto"
                onError={(e) => {
                  // Fallback if the hosted logo is unavailable
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <div className="hidden w-9 h-9 bg-white rounded-lg items-center justify-center">
                <span className="text-oem-navy font-display text-lg">OS</span>
              </div>
              <span className="text-[11px] font-semibold text-oem-300 tracking-[0.14em] uppercase border-l border-white/15 pl-3">
                KPI Dashboard
              </span>
            </div>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-4">
              {lastSynced && (
                <span className="text-xs text-oem-300" title="Last data refresh">
                  Updated {formatTime(lastSynced)}
                </span>
              )}
              <button
                onClick={onRefresh}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-oem-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>

              {user?.isAdmin && (
                <button
                  onClick={onToggleImpersonate}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-oem-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                  title={impersonating ? 'Return to admin view' : 'Preview as a read-only viewer'}
                >
                  {impersonating ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {impersonating ? 'Exit Viewer Mode' : 'View as Viewer'}
                </button>
              )}

              {isAdmin && (
                <button
                  onClick={() => setShowDevTools(!showDevTools)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-oem-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                >
                  <Database className="w-4 h-4" />
                  Data
                </button>
              )}

              {user && (
                <div className="flex items-center gap-3 pl-4 border-l border-white/15">
                  <div className="text-right">
                    <p className="text-sm font-medium text-white">{user.name}</p>
                    <p className="text-xs text-oem-300">{user.email}</p>
                  </div>
                  {user.isAdmin ? (
                    <ShieldCheck className="w-5 h-5 text-status-automation" />
                  ) : (
                    <Shield className="w-5 h-5 text-oem-300" />
                  )}
                  <button
                    onClick={() => auth.logout()}
                    className="p-2 rounded-lg hover:bg-white/10 text-oem-300 hover:text-white transition-colors"
                    title="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-white/10 text-white"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 px-4 py-3 space-y-2 bg-oem-navy">
            <button
              onClick={() => { onRefresh(); setMobileMenuOpen(false); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-oem-300 rounded-lg hover:bg-white/10 hover:text-white"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh Data
            </button>
            {user && (
              <div className="pt-2 border-t border-white/10">
                <div className="flex items-center gap-2 px-3 py-2">
                  <User className="w-4 h-4 text-oem-300" />
                  <span className="text-sm text-oem-100">{user.email}</span>
                  {user.isAdmin && <span className="text-xs bg-status-automation/20 text-status-automation px-1.5 py-0.5 rounded">Admin</span>}
                </div>
                <button
                  onClick={() => auth.logout()}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-status-escalation rounded-lg hover:bg-white/10"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Impersonation banner */}
      {impersonating && user?.isAdmin && (
        <div className="bg-accent-100 border-b border-accent-200 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <p className="text-sm text-accent-800 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Viewer mode — you're seeing the dashboard as a read-only user.
            </p>
            <button onClick={onToggleImpersonate} className="text-sm font-medium text-accent-700 hover:underline">
              Exit
            </button>
          </div>
        </div>
      )}

      {/* Dev Tools Panel */}
      {showDevTools && isAdmin && (
        <div className="bg-oem-100 border-b border-oem-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-4 flex-wrap">
            <span className="text-sm font-medium text-oem-600">Admin Tools:</span>
            <button
              onClick={onOpenAdminPanel}
              className="text-sm text-oem-800 font-medium hover:underline"
            >
              Admin Panel
            </button>
            <button
              onClick={() => { if (confirm('Clear all override data?')) { db.clearAll(); onRefresh(); } }}
              className="text-sm text-status-escalation hover:underline"
            >
              Clear All Overrides
            </button>
            <button
              onClick={() => { db.clearDeleted(); onRefresh(); }}
              className="text-sm text-status-intervention hover:underline"
            >
              Restore Deleted
            </button>
            <button
              onClick={() => { if (confirm('Clear the local analytics cache? The next refresh will re-sync everything.')) { analyticsCache.clear(); onRefresh(); } }}
              className="text-sm text-oem-600 hover:underline"
            >
              Clear Cache
            </button>
            <span className="text-xs text-oem-muted ml-auto">
              Project: 436481 | Region: US
            </span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 space-y-6 flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-oem-navy border-t border-white/10 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-oem-300">
            © {new Date().getFullYear()} OEMSTREAM, CORP. All rights reserved.
          </p>
          <p className="text-xs text-oem-300">
            Live analytics · US Region
          </p>
        </div>
      </footer>
    </div>
  );
}
